/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { jsx, cssMap, cx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box, Stack, Text, Inline } from '@atlaskit/primitives/compiled';
import Pressable from '@atlaskit/primitives/pressable';
import Spinner from '@atlaskit/spinner';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';

import { ContentIcon } from '@confluence/icons/entry-points/ContentIcon.ts';
import { ContentTreeSearchBase } from '@confluence/content-tree-search/entry-points/ContentTreeSearchBase.ts';
import { useContentTitles } from './hooks/useContentTitles.ts';

const styles = cssMap({
	movePopupContent: {
		padding: token('space.150'),
		width: '280px',
	},
	searchHint: {
		padding: token('space.100'),
		color: token('color.text.subtlest'),
	},
	moveButtonOpen: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
	},
	fallbackSectionLabel: {
		padding: token('space.050'),
	},
	fallbackParentRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.075'),
		padding: token('space.100'),
		width: '100%',
		backgroundColor: 'transparent',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
		borderRadius: token('radius.large'),
	},
	fallbackParentTitle: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		minWidth: '0%',
		flex: 1,
		textAlign: 'start',
	},
	fallbackParentIcon: {
		flexShrink: 0,
	},
	spinnerContainer: {
		display: 'flex',
		justifyContent: 'center',
		padding: token('space.150'),
	},
});

const i18n = defineMessages({
	moveButton: {
		id: 'side-navigation.review-content-suggestions-modal.move-button',
		defaultMessage: 'Move',
		description: 'Label for the move button on a suggestion item row',
	},
	moveToTitle: {
		id: 'side-navigation.review-content-suggestions-modal.move-to-title',
		defaultMessage: 'Move to',
		description: 'Title shown at the top of the move popup',
	},
	searchHint: {
		id: 'side-navigation.review-content-suggestions-modal.move-search-hint',
		defaultMessage: 'Search',
		description: 'Hint text shown in the move popup before the user has typed a search query',
	},
	suggested: {
		id: 'side-navigation.review-content-suggestions-modal.move-suggested',
		defaultMessage: 'Suggested',
		description:
			'Section label shown above the list of suggested fallback parent locations in the move popup',
	},
});

type FallbackParentRowProps = {
	parentId: number;
	meta: { type: string; title?: string } | undefined;
	title: string | undefined;
	onMoveItemClick: (parentId: number) => void;
};

const FallbackParentRow = ({ parentId, meta, title, onMoveItemClick }: FallbackParentRowProps) => {
	const handleClick = useCallback(() => {
		onMoveItemClick(parentId);
	}, [onMoveItemClick, parentId]);

	return (
		<Pressable
			key={parentId}
			xcss={styles.fallbackParentRow}
			onClick={handleClick}
			testId={`suggestion-move-fallback-parent-${parentId}`}
		>
			<Inline xcss={styles.fallbackParentIcon} alignBlock="center">
				<ContentIcon type={meta?.type ?? 'page'} />
			</Inline>
			<Box xcss={styles.fallbackParentTitle}>
				<Text>{meta?.title ?? title}</Text>
			</Box>
		</Pressable>
	);
};

export type SuggestionMovePopupProps = {
	suggestionId: string;
	spaceKey?: string;
	isOpen: boolean;
	onOpenChange: (isOpen: boolean) => void;
	onMoveSuggestion: (suggestionId: string, newParentId: number) => void;
	fallbackParents?: number[];
	currentParentId?: number;
};

export const SuggestionMovePopup = ({
	suggestionId,
	spaceKey,
	isOpen,
	onOpenChange,
	onMoveSuggestion,
	fallbackParents,
	currentParentId,
}: SuggestionMovePopupProps) => {
	const { formatMessage } = useIntl();

	const filteredFallbackParents = useMemo(
		() => (fallbackParents ?? []).filter((id) => id !== currentParentId),
		[fallbackParents, currentParentId],
	);

	const fallbackParentIdStrings = useMemo(
		() => filteredFallbackParents.map(String),
		[filteredFallbackParents],
	);

	const { titlesByContentId, contentMetaById, isLoading } = useContentTitles(
		isOpen ? fallbackParentIdStrings : [],
	);

	const handleMoveItemClick = useCallback(
		(idOrAri: string | number) => {
			const str = String(idOrAri);
			const rawId = str.includes('/') ? (str.split('/').pop() ?? str) : str;
			const numId = Number(rawId);
			if (!rawId || isNaN(numId)) return;
			onMoveSuggestion(suggestionId, numId);
			onOpenChange(false);
		},
		[onMoveSuggestion, onOpenChange, suggestionId],
	);

	const renderFallbackParentList = () => {
		if (isLoading) {
			return (
				<Box xcss={styles.spinnerContainer}>
					<Spinner size="small" />
				</Box>
			);
		}
		const accessibleParents = filteredFallbackParents.filter((parentId) =>
			contentMetaById.has(String(parentId)),
		);

		if (accessibleParents.length === 0) {
			return (
				<Box xcss={styles.searchHint}>
					<Text color="color.text.subtlest">{formatMessage(i18n.searchHint)}</Text>
				</Box>
			);
		}

		return (
			<Stack>
				<Box xcss={styles.fallbackSectionLabel}>
					<Text size="small" color="color.text.subtlest" weight="semibold">
						{formatMessage(i18n.suggested)}
					</Text>
				</Box>
				{accessibleParents.map((parentId) => (
					<FallbackParentRow
						key={parentId}
						parentId={parentId}
						meta={contentMetaById.get(String(parentId))}
						title={titlesByContentId.get(String(parentId))}
						onMoveItemClick={handleMoveItemClick}
					/>
				))}
			</Stack>
		);
	};

	const fallbackParentRows =
		isOpen && filteredFallbackParents.length > 0 ? (
			renderFallbackParentList()
		) : (
			<Box xcss={styles.searchHint}>
				<Text color="color.text.subtlest">{formatMessage(i18n.searchHint)}</Text>
			</Box>
		);

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => onOpenChange(false)}
			placement="bottom-start"
			zIndex={800}
			content={() => (
				<Box xcss={styles.movePopupContent}>
					<Text weight="bold">{formatMessage(i18n.moveToTitle)}</Text>
					<ContentTreeSearchBase
						spaceKey={spaceKey ?? ''}
						onItemClick={handleMoveItemClick}
						contentId=""
						placeholder={formatMessage(i18n.searchHint)}
					>
						{fallbackParentRows}
					</ContentTreeSearchBase>
				</Box>
			)}
			trigger={(triggerProps) => (
				<Box xcss={cx(isOpen && styles.moveButtonOpen)}>
					<Button
						{...triggerProps}
						appearance="subtle"
						spacing="compact"
						onClick={() => onOpenChange(!isOpen)}
						testId={`review-content-suggestion-move-button-${suggestionId}`}
					>
						{formatMessage(i18n.moveButton)}
					</Button>
				</Box>
			)}
		/>
	);
};
